import React, { useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { useContent } from '../context/ContentContext';
import { resolveImg } from '../api';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const BijouxSection = () => {
  const { t, lang } = useLang();
  const { content } = useContent();
  const bijoux = content?.bijoux || [];
  const [active, setActive] = useState(0);

  if (bijoux.length === 0) return null;

  const safeActive = active % bijoux.length;
  const current = bijoux[safeActive];

  const next = () => setActive((i) => (i + 1) % bijoux.length);
  const prev = () => setActive((i) => (i - 1 + bijoux.length) % bijoux.length);

  return (
    <section id="bijoux" className="section-pad bg-white relative overflow-hidden border-t border-[#E8E2D5]">
      <div className="container-tight">
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <span className="eyebrow">{t.bijoux.eyebrow}</span>
          <h2 className="h-display mt-6" style={{ fontSize: 'clamp(40px, 5vw, 64px)' }}>
            {t.bijoux.title}
          </h2>
          <p className="text-[15px] text-[#3A3A3A] leading-[1.8] mt-6">{t.bijoux.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Featured large image */}
          <div className="lg:col-span-7 relative">
            <div className="aspect-square bg-[#FAF8F4] image-hover">
              <img
                src={resolveImg(current.img)}
                alt={lang === 'fr' ? current.nameFr : current.nameEn}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute top-4 left-4 bg-white/95 backdrop-blur px-4 py-2 border border-[#E8E2D5]">
              <div className="text-[9px] tracking-[0.32em] uppercase text-[#957340]">
                {String(safeActive + 1).padStart(2, '0')} / {String(bijoux.length).padStart(2, '0')}
              </div>
            </div>
          </div>

          {/* Right: details + thumbnails */}
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#957340] mb-3">
              {current.material || 'Collection'}
            </div>
            <h3 className="h-display text-[44px] leading-[1.1] mb-4">
              {lang === 'fr' ? current.nameFr : current.nameEn || current.nameFr}
            </h3>
            <div className="gold-line w-24 my-6" />
            <p className="text-[14px] text-[#3A3A3A] leading-[1.8] mb-8">
              {lang === 'fr'
                ? 'Une pièce d\u2019exception sélectionnée par notre atelier. Disponible sur demande en boutique.'
                : 'An exceptional piece selected by our studio. Available on request in our shop.'}
            </p>

            <div className="flex items-center gap-3 mb-8">
              <button
                onClick={prev}
                className="w-11 h-11 border border-[#1A1A1A] flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white transition-colors"
                aria-label="prev"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={next}
                className="w-11 h-11 border border-[#1A1A1A] flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white transition-colors"
                aria-label="next"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            <div className="grid grid-cols-4 gap-3">
              {bijoux.map((b, i) => (
                <button
                  key={b.id}
                  onClick={() => setActive(i)}
                  className={`aspect-square overflow-hidden border-2 transition-all duration-300 ${
                    i === safeActive ? 'border-[#957340]' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                  aria-label={`bijou ${i + 1}`}
                >
                  <img src={resolveImg(b.img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BijouxSection;
